import {
    Box,
    Button,
    Center, Container, Flex,
    FormLabel, Heading, Input, ListItem, Select, Stack,
    Text,
    Textarea,
    UnorderedList
} from '@chakra-ui/react';
import { Divider } from 'antd';
import { CheckboxContainer, CheckboxControl } from 'formik-chakra-ui';
import { useState } from 'react';
import * as yup from 'yup';
import { ExperienceInput, useAddExperienceMutation } from '../../../generated/graphql';
import { FormStep } from '../MultiStepForm';

export default function Experience(props: any) {
    const { experiences, setExperiences } = props;
    const emptyExperience = {
        employer: '',
        title: '',
        start_date: '',
        end_date: '',
        description: '',
        is_current: false,
        order: experiences.length + 1,
        startYear: '',
        startMonth: '',
        endMonth: '',
        endYear: '',
    };
    const [errors, setErrors] = useState<string[]>([]);
    const [newExperience, setNewExperience] = useState(emptyExperience);
    const [addExperience] = useAddExperienceMutation();
    const onAddExperienceSubmit = async (experience: any) => {
        try {
            const validationSchema= yup.object().shape({
                is_current: yup.boolean(),
                title: yup.string().required('Please enter a valid title.'),
                employer: yup.string().required('Please enter a valid employer.'),
                startMonth: yup.string().required('Please enter a valid start month.'),
                startYear: yup.string().required('Please enter a valid start year.'),
                endMonth: yup.string().when('is_current', {
                    is: false,
                    then: yup.string().required('Please enter a valid end month.'),
                }),
                endYear: yup.string().when('is_current', {
                    is: false,
                    then: yup.string().required('Please enter a valid end year.'),
                }),
                description: yup.string().required('Please enter a valid description.'),
            });
        setExperiences([...experiences, experience]);
        let input: ExperienceInput = {
            employer: experience.employer,
            title: experience.title,
            start_date: experience.start_date,
            end_date: !experience.is_current ? experience.end_date : null,
            description: experience.description,
            is_current: experience.is_current,
            order: experience.order
        };
        await validationSchema.validate(experience, {abortEarly: false})
        addExperience({
            variables: {
                input
            }
        });
        setErrors([]);
        setNewExperience(emptyExperience);
    } catch (err) {
        setErrors([...err.errors]);
      }
    };
    
    return (
        <FormStep stepName="Experience">
            <Heading as="h2" size="lg" mb={4}>
                What work experience do you have?
            </Heading>

            {experiences.map((experience: any, index: number) => {
                return ExperienceDisplay(experiences[index]);
            })}
            <Divider />
            {ExperienceForm(newExperience, setNewExperience, onAddExperienceSubmit, errors)}
        </FormStep>
    );
}

function ExperienceForm(
  newExperience: any,
  setNewExperience: (experience: any) => void,
  onAddExperienceSubmit: any,
  errors: string[]
) {
  const year = new Date().getFullYear();

  const yearArray: string[] = [];
  for (const x of Array(10).keys()) {
      yearArray.push((year - x).toString());
  }
  const yearChoices = () => {
      return yearArray.map((year) => {
          return (
              <option value={year}>
                  {year}
              </option>
          );
      });
  };

  const handleInput = (e: any) => {
    const { keyCode, target } = e;
    const { selectionStart, value } = target;

    const bullet = '\u2022';
    const bulletWithSpace = `${bullet} `;
    const enter = 13;

    if (keyCode === enter) {
        target.value = [...value]
            .map((c, i) => (i === selectionStart - 1 ? `\n${bulletWithSpace}     ` : c))
            .join('');

        target.selectionStart = selectionStart + bulletWithSpace.length;
        target.selectionEnd = selectionStart + bulletWithSpace.length;
    }

    if (value[0] !== bullet) {
        target.value = `${bulletWithSpace}${value}`;
    }
};

  return (
      <Center>
          <Stack gap="12px" w="530px">
              <Heading align="left" fontSize="24px">
                  New Experience
              </Heading>
              <Stack>
                  <FormLabel textAlign="left" mb="-2" fontWeight={600} fontSize="15">
                      Title
                  </FormLabel>
                  <Input
                      name="title"
                      align="center"
                      borderRadius="8px"
                      height="48px"
                      fontSize="15px"
                      _invalid={{
                          fontSize: '14px',
                          border: 'solid 1px red'
                      }}
                      fontWeight={400}
                      width={'100%'}
                      onChange={(event: any) => {
                          const { value } = event.target;
                          const newExp = { ...newExperience, title: value };
                          setNewExperience(newExp);
                      }}
                      isInvalid={errors.includes('Please enter a valid title.') && newExperience.title === ''}
                      errorBorderColor='red.300'
                      placeholder={errors.includes('Please enter a valid title.') ? 'Please enter a valid title.' : ''}
                      value={newExperience.title}
                  />
              </Stack>
              <Stack>
                  <FormLabel textAlign="left" mb="-2" fontWeight={600} fontSize="15">
                      Employer
                  </FormLabel>
                  <Input
                      name="employer"
                      align="center"
                      borderRadius="8px"
                      height="48px"
                      fontSize="15px"
                      _invalid={{
                          fontSize: '14px',
                          border: 'solid 1px red'
                      }}
                      fontWeight={400}
                      width={'100%'}
                      onChange={(event: any) => {
                          const { value } = event.target;
                          const newExp = { ...newExperience, employer: value };
                          setNewExperience(newExp);
                      }}
                      isInvalid={errors.includes('Please enter a valid employer.') && newExperience.employer === ''}
                      errorBorderColor='red.300'
                      placeholder={errors.includes('Please enter a valid employer.') ? 'Please enter a valid employer.' : ''}
                      value={newExperience.employer}
                  />
              </Stack>
              <Stack w="530px" gap="12px">
                  <Stack>
                      <Text
                          textAlign="left"
                          fontWeight={600}
                          mb="-1"
                          fontSize="15"
                          lineHeight="20px"
                      >
                          Start Date
                      </Text>
                      <Flex w="100%">
                          <Select
                              name="startMonth"
                              w="full"
                              onChange={(event: any) => {
                                  const {target} = event;
                                  const {value} = target;
                                  const newExp = {
                                      ...newExperience,
                                      start_date: value ? `${value}-${newExperience.startMonth}` : '',
                                      startYear: newExperience.startYear,
                                      startMonth: value
                                  };
                                  setNewExperience(newExp);
                              }}
                                isInvalid={errors.includes('Please enter a valid start year.') && newExperience.startYear === ''}
                                errorBorderColor='red.300'
                                value={newExperience.startMonth}
                          >
                              <option value="">
                                 Month 
                              </option>
                              <option value="01">
                                  January
                              </option>
                              <option value="02">
                                  February
                              </option>
                              <option value="03">
                                  March
                              </option>
                              <option value="04">
                                  April
                              </option>
                              <option value="05">
                                  May
                              </option>
                              <option value="06">
                                  June
                              </option>
                              <option value="07">
                                  July
                              </option>
                              <option value="08">
                                  August
                              </option>
                              <option value="09">
                                  September
                              </option>
                              <option value="10">
                                  October
                              </option>
                              <option value="11">
                                  November
                              </option>
                              <option value="12">
                                  December
                              </option>
                          </Select>
                          <Select
                              name="StartYear"
                              w="full"
                              onChange={(event: any) => {
                                  const {target} = event;
                                  const {value} = target;

                                  const newExp = {
                                      ...newExperience,
                                      start_date: value ? `${value}-${newExperience.startMonth}` : '',
                                      startYear: value,
                                      startMonth: newExperience.startMonth
                                  };
                                  setNewExperience(newExp);
                              }}
                              isInvalid={errors.includes('Please enter a valid start year.') && newExperience.startYear === ''}
                              errorBorderColor='red.300'
                              value={newExperience.startYear}
                          >
                              <option value="">Year</option>
                              {yearChoices()}
                          </Select>
                      </Flex>
                  </Stack>
                  <Box border="1px solid #E1E0E3" padding="16px" borderRadius="8px">
                      <CheckboxContainer name="currentCheck" ml="-6" w="570px">
                          <CheckboxControl
                              className="round"
                              name="current"
                              value={newExperience.is_current}
                              isChecked={newExperience.is_current}
                              onChange={(event: any) => {
                                  const newExp = {
                                      ...newExperience,
                                      end_date: '',
                                      is_current: newExperience.is_current ? false : true,
                                  };
                                  setNewExperience(newExp);
                              }}
                          >
                              <Text>I currently work here</Text>
                          </CheckboxControl>
                      </CheckboxContainer>
                  </Box>
                  {!newExperience.is_current && (
                      <>
                          <Stack>
                              <Text
                                  textAlign="left"
                                  fontWeight={600}
                                  mb="-1"
                                  fontSize="15"
                                  lineHeight="20px"
                              >
                                  End Date
                              </Text>
                              <Flex w="100%">
                                  <Select
                                      name="endMonth"
                                      w="full"
                                      onChange={(event: any) => {
                                          const {target} = event;
                                          const {value} = target;
                                          const newExp = {
                                              ...newExperience,
                                              end_date: value ? `${newExperience.endYear}-${value}` : '',
                                              endYear: newExperience.endYear,
                                              endMonth: value
                                          };
                                          setNewExperience(newExp);
                                      }}
                                      isInvalid={errors.includes('Please enter a valid end month.') && newExperience.endMonth === ''}
                                      errorBorderColor='red.300'
                                      value={newExperience.endMonth}
                                  >
                                      <option value="">Month</option>
                                      <option value="01">
                                          January
                                      </option>
                                      <option value="02">
                                          February
                                      </option>
                                      <option value="03">
                                          March
                                      </option>
                                      <option value="04">
                                          April
                                      </option>
                                      <option value="05">
                                          May
                                      </option>
                                      <option value="06">
                                          June
                                      </option>
                                      <option value="07">
                                          July
                                      </option>
                                      <option value="08">
                                          August
                                      </option>
                                      <option value="09">
                                          September
                                      </option>
                                      <option value="10">
                                          October
                                      </option>
                                      <option value="11">
                                          November
                                      </option>
                                      <option value="12">
                                          December
                                      </option>
                                  </Select>
                                  <Select
                                      name="endYear"
                                      w="full"
                                      onChange={(event: any) => {
                                        const {target} = event;
                                        const {value} = target;
                                        const newExp = {
                                            ...newExperience,
                                            end_date: value ? `${value}-${newExperience.endMonth}` : '',
                                            endYear: value,
                                            endMonth: newExperience.endMonth
                                        };
                                          setNewExperience(newExp);
                                      }}
                                      isInvalid={errors.includes('Please enter a valid end year.') && newExperience.endYear === ''}
                                      errorBorderColor='red.300'
                                      value={newExperience.endYear}
                                  >
                                      <option value="">Year</option>
                                      {yearChoices()}
                                  </Select>
                              </Flex>
                          </Stack>
                      </>
                  )}
                  <Input hidden={true} name="order" value={newExperience.order} readOnly={true} />
                  <Stack>
                      <Text
                          textAlign="left"
                          fontWeight={600}
                          mb="-1"
                          fontSize="15"
                          lineHeight="20px"
                      >
                            Describe Your Role
                      </Text>
                      <Textarea onKeyUp={handleInput} name={newExperience.description}
                                            onChange={(event: any) => {
                                                const { value } = event.target;
                                                const newExp = { ...newExperience, description: value };
                                                setNewExperience(newExp);
                                            }}
                                            isInvalid={errors.includes('Please enter a valid description.') && newExperience.description === ''}
                                            errorBorderColor='red.300'
                                            placeholder={errors.includes('Please enter a valid description.') ? 'Please enter a valid description.' : ''}
                                            value={newExperience.description}
                      >
                            
                      </Textarea>
                  </Stack>
              </Stack>
              <Button onClick={() => onAddExperienceSubmit(newExperience)}>Add Experience</Button>
              <Text>Click add experience before proceeding.</Text>
          </Stack>
      </Center>
  );
}


function ExperienceDisplay(experience: any) {

    let startYear = '';
    let startMonth = '';
    let endYear = '';
    let endMonth = '';
    let roleDescription = [];

    const getMonthName = (month: number) => {
        const date = new Date();
        date.setMonth(month-1);
        
        return date.toLocaleString('default', { month: 'long' });
    }


    if (experience.start_date) {
        startYear = experience.start_date.split('-')[0];
        startMonth = getMonthName(parseInt(experience.start_date.split('-')[1]));
    }

    if (experience.end_date) {
        endYear = experience.end_date.split('-')[0];
        endMonth = getMonthName(parseInt(experience.end_date.split('-')[1]))
    } else if (experience.is_current){
        endYear = 'Present';
        endMonth = '';
    }

    if (experience.description) {
        roleDescription = experience.description.split(`\n`).map((description: string) => {
            return (
                <ListItem>{description.split('\u2022')}</ListItem>
            )
        });
    }

    return (
        <Container key={experience.id}>
        <Box border='1px solid #EBEBEC' borderRadius='8px' padding='24px' gap='24px' mb={2}>
            <Flex justify='space-between' mb={2}>
            <Heading align='left' fontSize='20px' fontWeight={800} lineHeight='28px'>{experience.employer}</Heading>
            <Text fontWeight={500} fontSize='13px' lineHeight='20px' color='#686672'>{startMonth}{' '}{startYear}{' '}-{' '}{endMonth}{' '}{endYear}</Text>
            </Flex>
            <Text align='left' mb={3}>{experience.title}</Text>
            <Stack>
            <UnorderedList align='left' paddingLeft='.5rem' spacing={3} textAlign="left">{roleDescription}</UnorderedList>
            </Stack>
        </Box>
        </Container>
    );
}